import React, { FC } from 'react';
import { Typography, Box, Select, MenuItem, FormControl, SelectChangeEvent } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { TicketsCountSelector, TicketsParamsSelector } from '../../../../store/tickets/selectors';
import { changeTicketsParamsPage, changeTicketParamsPageSize } from '../../../../store/tickets';
import { tt } from '@i18n/tt';
import { ThunkGetTickets } from '../../../../store/tickets/thunks';
import useStyles from './styles';
import PaginationButton, { ButtonType } from './components/PaginationButton';

const TicketsPaginationRounded: FC = () => {
  const params = useAppSelector(TicketsParamsSelector);
  const ticketsCount = useAppSelector(TicketsCountSelector);
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const totalPages = Math.max(1, Math.ceil(ticketsCount / params.pageSize));
  const currentPage = params.page + 1;
  const startEntry = ticketsCount === 0 ? 0 : params.page * params.pageSize + 1;
  const endEntry = Math.min((params.page + 1) * params.pageSize, ticketsCount);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number): void => {
    const newPageNumber = newPage - 1;
    dispatch(changeTicketsParamsPage(newPageNumber));
    dispatch(ThunkGetTickets({ params: { ...params, page: newPageNumber } }));
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>): void => {
    const newPageSize = +event.target.value;
    dispatch(changeTicketParamsPageSize(newPageSize));
    // Reset to first page when changing page size
    dispatch(changeTicketsParamsPage(0));
    dispatch(ThunkGetTickets({ params: { ...params, pageSize: newPageSize, page: 0 } }));
  };

  return (
    <Box className={classes.paginationContainer}>
      <Box className={classes.leftSection}>
          <Typography className={classes.paginationText} fontSize="16px">
            {tt('table.showing')}{' '}
            <span className={classes.paginationNumbers}>
              {startEntry}-{endEntry} of {ticketsCount}
            </span>{' '}
            {tt('table.entries')}
          </Typography>
        <FormControl size="small" className={classes.pageSizeSelect}>
          <Select
            value={params.pageSize}
            onChange={handlePageSizeChange}
            displayEmpty
            className={classes.select}
            size="small"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Pagination
        count={totalPages}
        page={currentPage}
        onChange={handlePageChange}
        shape="rounded"
        color="primary"
        className={classes.pagination}
        renderItem={item => (
          <PaginationItem
            slots={{
              previous: () => <PaginationButton buttonType={ButtonType.Previous} />,
              next: () => <PaginationButton buttonType={ButtonType.Next} />,
            }}
            {...item}
          />
        )}
      />
    </Box>
  );
};

export default TicketsPaginationRounded;
